import React, { useState, useCallback } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseAdminScreen, AdminEmptyScreenLayout } from '@/components';
import { AdminStackParamList } from '@/navigation/admin/AdminNavigator';
import { AdminErrorBoundary } from '@/components/common/admin/mobile/AdminErrorBoundary';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useThreadModeration } from '@/hooks/admin/useThreadModeration';
import { ThreadModerationList } from '@/components/admin/corkBoard/ThreadModerationList';
import { EditThreadTagsModal } from '@/components/admin/corkBoard/EditThreadTagsModal';
import { AdminPostFormModal } from '@/components/admin/corkBoard/AdminPostFormModal';
import { AdminSurveyEditModal } from '@/components/admin/corkBoard/AdminSurveyEditModal';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { showConfirm } from '@/utils/common/alert';
import { DesktopSurveysManagementView } from './desktop/DesktopSurveysManagementView';
import type { CorkBoardThreadSummary, CreateSurveyThreadRequest } from '@/types/residents/corkBoard';

type SurveysManagementScreenProps = StackScreenProps<AdminStackParamList, 'SurveysManagement'>;

export const SurveysManagementScreen: React.FC<SurveysManagementScreenProps> = ({ navigation }) => {
  const { layoutMode } = useResponsive();
  const moderation = useThreadModeration({ kind: 'survey' });
  const { redesign, semantic } = useAdminColors();

  const [postModalVisible, setPostModalVisible] = useState(false);
  const [editTagsVisible, setEditTagsVisible] = useState(false);
  const [editingThread, setEditingThread] = useState<CorkBoardThreadSummary | null>(null);
  const [editSurveyVisible, setEditSurveyVisible] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<CorkBoardThreadSummary | null>(null);

  const handleOpenEditTags = useCallback((thread: CorkBoardThreadSummary) => {
    setEditingThread(thread);
    setEditTagsVisible(true);
  }, []);

  const handleCloseEditTags = useCallback(() => {
    setEditTagsVisible(false);
    setEditingThread(null);
  }, []);

  const handleSaveTags = useCallback(
    async (threadId: string, tagIds: number[]) => {
      const success = await moderation.handleUpdateThreadTags(threadId, tagIds);
      if (success) handleCloseEditTags();
    },
    [moderation, handleCloseEditTags],
  );

  const handleOpenPostModal = useCallback(() => {
    setPostModalVisible(true);
  }, []);

  const handleClosePostModal = useCallback(() => {
    setPostModalVisible(false);
  }, []);

  const handleSaveSurvey = useCallback(
    async (data: Omit<CreateSurveyThreadRequest, 'propertyId'>) => {
      const success = await moderation.handleCreateSurvey(data);
      if (success) handleClosePostModal();
    },
    [moderation, handleClosePostModal],
  );

  const handleViewResponses = useCallback(
    (thread: CorkBoardThreadSummary) => {
      navigation.navigate('SurveyResponses', { threadId: thread.id });
    },
    [navigation],
  );

  const handleDelete = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      const confirmed = await showConfirm(
        'Delete Survey',
        `Are you sure you want to delete "${thread.title}"? This action cannot be undone.`,
        'Delete',
        'Cancel',
      );
      if (confirmed) {
        await moderation.handleDeleteThread(thread.id);
      }
    },
    [moderation],
  );

  const handleOpenEditSurvey = useCallback((thread: CorkBoardThreadSummary) => {
    setEditingSurvey(thread);
    setEditSurveyVisible(true);
  }, []);

  const handleCloseEditSurvey = useCallback(() => {
    setEditSurveyVisible(false);
    setEditingSurvey(null);
  }, []);

  const handleSaveSurveyEdit = useCallback(
    async (data: { threadId: string; title: string; body: string; tagIds: number[] }) => {
      const success = await moderation.handleUpdateThread(data);
      if (success) handleCloseEditSurvey();
    },
    [moderation, handleCloseEditSurvey],
  );

  const handleCloseSurvey = useCallback(
    async (thread: CorkBoardThreadSummary) => {
      const confirmed = await showConfirm(
        'Close Survey',
        `Close "${thread.title}" now? Residents will no longer be able to respond. This can't be undone.`,
        'Close Survey',
        'Keep Open',
      );
      if (confirmed) {
        await moderation.handleCloseSurvey(thread.id);
      }
    },
    [moderation],
  );

  if (layoutMode === 'desktop') {
    return (
      <AdminErrorBoundary context="SurveysManagementDesktop">
        <DesktopSurveysManagementView
          moderation={moderation}
          postModalVisible={postModalVisible}
          editTagsVisible={editTagsVisible}
          editingThread={editingThread}
          onOpenEditTags={handleOpenEditTags}
          onCloseEditTags={handleCloseEditTags}
          onSaveTags={handleSaveTags}
          onOpenPostModal={handleOpenPostModal}
          onClosePostModal={handleClosePostModal}
          onSaveSurvey={handleSaveSurvey}
          onDelete={handleDelete}
          onViewResponses={handleViewResponses}
          onEdit={handleOpenEditSurvey}
          onClose={handleCloseSurvey}
          editSurveyVisible={editSurveyVisible}
          editingSurvey={editingSurvey}
          onCloseEditSurvey={handleCloseEditSurvey}
          onSaveSurveyEdit={handleSaveSurveyEdit}
          onSurveyConfigChange={moderation.handleUpdateSurveyConfig}
        />
      </AdminErrorBoundary>
    );
  }

  return (
    <AdminErrorBoundary context="SurveysManagement">
      <BaseAdminScreen
        screenName="SurveysManagement"
        navigation={navigation}
        showPlaceholder={false}
        scrollEnabled={false}
        contentContainerStyle={styles.baseScreenContent}
      >
        <AdminEmptyScreenLayout
          title="Surveys"
          emptyStateType="corkBoardThreads"
          onAddPress={handleOpenPostModal}
          showAddButton={true}
          buttonType="add"
          hasContent={true}
          disableScrollView={true}
        >
          {moderation.error && (
            <View
              style={[
                styles.errorBanner,
                { backgroundColor: redesign.accent.redBg, borderBottomColor: semantic.status.error },
              ]}
            >
              <Text style={[styles.errorBannerText, { color: semantic.status.error }]}>
                Failed to load surveys.
              </Text>
              <TouchableOpacity onPress={moderation.refetch}>
                <Text style={[styles.errorRetryText, { color: semantic.status.error }]}>Retry</Text>
              </TouchableOpacity>
            </View>
          )}

          <ThreadModerationList
            threads={moderation.threads}
            isLoading={moderation.isLoading}
            isMutating={moderation.isMutating}
            onPin={moderation.handlePinThread}
            onLock={moderation.handleLockThread}
            onEditTags={handleOpenEditTags}
            onDelete={handleDelete}
            onViewResponses={handleViewResponses}
            onEdit={handleOpenEditSurvey}
            onClose={handleCloseSurvey}
            onRefresh={moderation.refetch}
            isRefreshing={moderation.isManualRefreshing}
            onLoadMore={moderation.loadMore}
            hasNextPage={moderation.hasNextPage}
            isFetchingMore={moderation.isFetchingNextPage}
          />
        </AdminEmptyScreenLayout>

        <EditThreadTagsModal
          visible={editTagsVisible}
          thread={editingThread}
          availableTags={moderation.tags}
          isSaving={moderation.isUpdatingTags}
          onSave={handleSaveTags}
          onClose={handleCloseEditTags}
        />
        <AdminPostFormModal
          visible={postModalVisible}
          kind="survey"
          availableTags={moderation.tags}
          isSavingSurvey={moderation.isCreatingSurvey}
          onSaveSurvey={handleSaveSurvey}
          onClose={handleClosePostModal}
        />
        <AdminSurveyEditModal
          visible={editSurveyVisible}
          thread={editingSurvey}
          availableTags={moderation.tags}
          isSaving={moderation.isUpdatingSurvey}
          onSave={handleSaveSurveyEdit}
          onConfigChange={moderation.handleUpdateSurveyConfig}
          onViewResponses={handleViewResponses}
          onClose={handleCloseEditSurvey}
        />
      </BaseAdminScreen>
    </AdminErrorBoundary>
  );
};

const styles = StyleSheet.create({
  baseScreenContent: { paddingHorizontal: 0, paddingTop: 0, paddingBottom: 0, flexGrow: 1 },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  errorBannerText: { fontSize: 13 },
  errorRetryText: { fontSize: 13, fontWeight: '600', textDecorationLine: 'underline' },
});
